import { Row, Col, Card, Table, Button, Input, DatePicker, Tooltip, Avatar, Switch } from "antd";
import { useState, useEffect, useRef } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import moment from 'moment';
import { useNavigate, useLocation } from "react-router-dom";
import ShowTotal from "../../components/ShowTotal";
import apiPath from "../../constants/apiPath";
import From from "./From";
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import ConfirmationBox from "../../components/ConfirmationBox";
import { getSortOrder } from "../../constants/conts";
import { EyeIcon, Logs, PencilLineIcon } from "lucide-react";
const Search = Input.Search;

function Index() {
  const sectionName = "User";
  const routeName = "user";

  const api = {
    status: apiPath.statusUser,
    addEdit: apiPath.addEditUser,
    list: apiPath.listUser
  };

  const { request } = useRequest();
  const { showConfirm } = ConfirmationBox();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  // ---- parse initial values from query string ----
  const initialPage = parseInt(query.get("page")) || 1;
  const initialPageSize = parseInt(query.get("pageSize")) || 20;
  const initialSearch = query.get("search") || "";
  const initialSort = query.get("sort") || "";

  // ---- state (use initial values) ----
  const [searchText, setSearchText] = useState(initialSearch);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState();

  const [sortBy, setSortBy] = useState(initialSort);

  const [pagination, setPagination] = useState({
    current: initialPage,
    pageSize: initialPageSize,
  });

  const lastFiltersRef = useRef({});
  const debouncedSearchText = useDebounce(searchText, 500);

  // ---- navigate to view while preserving query string ----
  const view = (id) => {
    navigate(`/${routeName}/view/${id}${location.search}`);
  };

  // ---- navigate to view while preserving query string ----
  const ActivityLog = (id) => {
    navigate(`/${routeName}/activity-logs/${id}${location.search}`);
  };

  // ---- sync state -> query string (so back/forward restores state) ----
  useEffect(() => {
    const params = new URLSearchParams();

    if (pagination.current > 1) params.set("page", pagination.current);
    if (pagination.pageSize && pagination.pageSize !== 20) params.set("pageSize", pagination.pageSize);
    if (debouncedSearchText) params.set("search", debouncedSearchText);
    if (sortBy) params.set("sort", sortBy);

    // replace so browser back works naturally
    navigate({ search: params.toString() }, { replace: true });
  }, [
    pagination.current,
    pagination.pageSize,
    debouncedSearchText,
    sortBy,
    navigate,
  ]);

  // ---- fetch data whenever relevant state changes ----
  useEffect(() => {
    setLoading(true);
    fetchData(pagination, lastFiltersRef.current, sortBy);
  }, [
    refresh,
    debouncedSearchText,
    sortBy,
    pagination.current,
    pagination.pageSize,
  ]);

  const fetchData = (pageInfo, filters, sortValue = '') => {
    const filterActive = filters ? filters.is_active : null;

    request({
      url:
        api.list +
        `?status=${filterActive ? filterActive.join(',') : ''}` +
        `&page=${pageInfo.current}` +
        `&limit=${pageInfo.pageSize}` +
        `&search=${debouncedSearchText}` +
        `&sort=${sortValue || ''}`,
      method: 'GET',
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.list.docs || []);
        setPagination(prev => ({
          ...prev,
          current: pageInfo.current,
          pageSize: pageInfo.pageSize,
          total: data.data.list.totalDocs,
        }));
      },
      onError: (error) => {
        console.log(error);
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      }
    });
  };

  // ---- table change handler: update state only (no direct fetch) ----
  const handleChange = (pag, filters, sorter) => {
    const order = sorter?.order;
    const field = sorter?.field;

    let nextSort = '';
    if (order) {
      nextSort = `${field}:${order === 'ascend' ? 'asc' : 'desc'}`;
    }

    lastFiltersRef.current = filters || {};
    setSortBy(nextSort);

    setPagination(prev => ({
      current: pag?.current || 1,
      pageSize: pag?.pageSize || prev.pageSize,
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // ---- columns: include Edit & View (View preserves query string) ----
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      sorter: true,
      sortOrder: getSortOrder(sortBy, "name"),

      render: (_, { name, _id }) => {
        const castName = name || 'Anonymous User';
        return (
          <>
            <Avatar className="cap" size={40}>{castName.charAt(0)}</Avatar>
            <a style={{ marginLeft: 12 }} className="cap avatar-text" onClick={() => view(_id)}>{castName}</a>
          </>
        );
      },
    },
    {
      title: "Phone Number",
      width: 200,
      dataIndex: "phone",
      key: "phone",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "phone"),
      render: (_, { phone, country_code }) => ((country_code ? "+" + country_code + "-" : "+91") + (phone || '')),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      width: 250,
      sorter: true,
      sortOrder: getSortOrder(sortBy, "email"),
      render: (_, { email }) => email || '-',
    },
    {
      title: "Status",
      key: "is_active",
      filters: [{ text: 'Active', value: true }, { text: 'Inactive', value: false }],
      render: (_, { is_active, _id, is_otp_verify }) => (
        is_otp_verify ? (
          <Switch
            checked={is_active}
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            loading={loading}
            onChange={(checked) =>
              showConfirm({
                record: _id,
                path: api.status,
                // onLoading: () => setLoading(true),
                onSuccess: () => setRefresh(prev => !prev),
                is_active: checked,
              })
            }
            className={is_active ? "custom-switch-checked" : "custom-switch-unchecked"}
          />
        ) : <span className="text-danger">OTP not verified</span>
      ),
    },
    {
      title: "Registered On",
      key: "created_at",
      dataIndex: "created_at",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "created_at"),
      render: (_, { created_at }) => moment(created_at).format('DD-MMM-YYYY'),
    },
    {
      title: "Action",
      fixed: 'right',
      render: (_, record) => (
        <>
          <Tooltip title={`Update ${sectionName}`} color="purple">
            <Button
              onClick={() => { setSelected(record); setVisible(true); }}
            >
              <PencilLineIcon size={15} />
            </Button>
          </Tooltip>

          <Tooltip title={`View ${sectionName}`} color="purple">
            <Button onClick={() => view(record._id)}>
              <EyeIcon size={15} />
            </Button>
          </Tooltip>

          <Tooltip title={`Activity Logs ${sectionName}`} color="purple">
            <Button
              onClick={() => ActivityLog(record._id)}>
              <Logs size={15} />
            </Button>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="tabled customerMain">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              variant={false}
              className="criclebox tablespace mb-24"
              title={sectionName + " Manager"}
              extra={
                <>
                  <div className="searchOuter">
                    <Search
                      className="mainSearch"
                      size="large"
                      allowClear
                      onChange={onSearch}
                      value={searchText}
                      onPressEnter={onSearch}
                      placeholder="Search By Name, Mobile, Email"
                    />
                  </div>

                  {/* <div className="button_group">
                    <Button onClick={() => { setVisible(true); setSearchText(''); }}>Add New {sectionName}</Button>
                  </div> */}
                </>
              }
            >
              <h4 className="text-right mb-1">{pagination.total ? ShowTotal(pagination.total) : ShowTotal(0)}</h4>

              <div className="table-responsive customPagination">
                <Table
                  loading={loading}
                  columns={columns}
                  dataSource={list}
                  rowKey={record => record._id}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ["10", "20", "30", "50", "100"],
                  }}
                  onChange={handleChange}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {visible && (
        <From
          path={api.addEdit}
          sectionName={sectionName}
          show={visible}
          hide={() => { setSelected(); setVisible(false); }}
          data={selected}
          refresh={() => {
            setRefresh(prev => !prev)
          }}
        />
      )}
    </>
  );
}

export default Index;
